import ic_check_box from '@material-design-icons/svg/outlined/check_box.svg?raw';
import ic_check_box_outline_blank from '@material-design-icons/svg/outlined/check_box_outline_blank.svg?raw';
import ic_select_all from '@material-design-icons/svg/outlined/select_all.svg?raw';
import { html, LitElement, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeSVG } from "lit/directives/unsafe-svg.js";
import { _DICT_ESCUELAS } from "../../../../data/services/Escuela";
import "../../../../ui/components-lit/Icon/Icon";
import _L from "../../../../utils/Labels";
import { MGroupP } from "../../models/MGroupP";
import "./ProximidadGroupFilter.scss";

/** Tiempo de espera (ms) tras el último cambio de selección antes de notificar al delegado. */
const SELECTION_EMIT_DEBOUNCE_MS = 2000;

/**
 * Lista de selección múltiple de grupos, agrupada por escuela.
 * Mantiene una selección "de trabajo" que se refleja de inmediato en la UI,
 * y notifica los cambios (uno por grupo modificado) a través de {@link onSelectGroup}
 * una vez que la selección se estabiliza durante {@link SELECTION_EMIT_DEBOUNCE_MS}.
 */
@customElement('app-proximidad-group-filter')
export class ProximidadGroupFilter extends LitElement {
    @property({ type: Array }) availableGroups: MGroupP[] = [];
    @property({ type: Array }) selectedGroupIds: number[] = [];
    @property({ type: Number }) currentGroupId: number | null = null;

    /** Delegado invocado una vez por cada IdGrupo cuyo estado de selección cambió, tras estabilizarse la selección. */
    public onSelectGroup: ((idGrupo: number) => void) | null = null;

    @state() private workingSelection: Set<number> = new Set();
    private confirmedSelection: Set<number> = new Set();
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;
    /** IdGrupo en el orden en que fueron seleccionados (el más reciente al final). Define el orden de los "grupos preferidos". */
    private selectionOrder: number[] = [];

    protected createRenderRoot(): HTMLElement | DocumentFragment {
        return this;
    }

    protected willUpdate(changedProps: Map<string, unknown>): void {
        if (changedProps.has('selectedGroupIds')) {
            const ids = this.selectedGroupIds || [];
            this.workingSelection = new Set(ids);
            this.confirmedSelection = new Set(ids);
            this.selectionOrder = this.selectionOrder.filter(id => this.workingSelection.has(id));
            ids.forEach(id => {
                if (!this.selectionOrder.includes(id)) this.selectionOrder.push(id);
            });
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.clearDebounce();
    }

    protected render(): TemplateResult {
        const schoolEntries = this.groupBySchool();
        const showSelectAll = (this.availableGroups?.length || 0) > 1;
        const allSelected = showSelectAll && this.availableGroups.every(g => this.workingSelection.has(g.IdGrupo));

        return html`
        <div class="group-filter-list">
            ${showSelectAll ? html`
                <div class=${"select-all-row" + (allSelected ? " all-selected" : "")}
                    @mousedown=${(e: Event) => e.stopPropagation()}
                    @click=${() => this.toggleSelectAll()}>
                    <app-icon-dumb class="select-all-icon">${unsafeSVG(ic_select_all)}</app-icon-dumb>
                    <span>${_L("ui.proximidad.seleccionar_todo")}</span>
                </div>
            ` : ""}
            ${schoolEntries.map(([idEsc, groups]) => html`
                <div class="school-block">
                    <div class="school-divider">${_DICT_ESCUELAS.get(idEsc)?.Nombre || "?"}</div>
                    <div class="school-groups">
                        ${groups.map(g => this.renderGroupItem(g))}
                    </div>
                </div>
            `)}
        </div>
        `;
    }

    private renderGroupItem(g: MGroupP): TemplateResult {
        const isSelected = this.workingSelection.has(g.IdGrupo);
        const isCurrent = this.currentGroupId != null && this.currentGroupId === g.IdGrupo;

        return html`
        <div class=${"group-list-item" + (isSelected ? " selected" : "")}
            @mousedown=${(e: Event) => e.stopPropagation()}
            @click=${(e: Event) => this.toggleGroup(g.IdGrupo, e)}>
                <app-icon-dumb class="check-icon">${unsafeSVG(isSelected ? ic_check_box : ic_check_box_outline_blank)}</app-icon-dumb>
                <span class="group-name">${g.NombreGrupo}</span>
                ${isCurrent ? html`<span class="current-flag">⚑</span>` : ""}
        </div>`;
    }

    private groupBySchool(): [number, MGroupP[]][] {
        const bySchool: Map<number, MGroupP[]> = new Map();
        (this.availableGroups || []).forEach(g => {
            const idEsc = g.IdEscuela || 0;
            if (!bySchool.has(idEsc)) bySchool.set(idEsc, []);
            bySchool.get(idEsc)!.push(g);
        });

        const currentEscId = this.currentGroupId != null
            ? (this.availableGroups || []).find(g => g.IdGrupo === this.currentGroupId)?.IdEscuela ?? null
            : null;

        for (const [, groups] of bySchool.entries()) {
            groups.sort((a, b) => {
                if (a.IdGrupo === this.currentGroupId) return -1;
                if (b.IdGrupo === this.currentGroupId) return 1;

                const aSelected = this.workingSelection.has(a.IdGrupo);
                const bSelected = this.workingSelection.has(b.IdGrupo);
                if (aSelected !== bSelected) return aSelected ? -1 : 1;
                if (aSelected && bSelected) {
                    // grupos preferidos: el seleccionado más recientemente va primero (justo después del actual)
                    return this.selectionOrder.indexOf(b.IdGrupo) - this.selectionOrder.indexOf(a.IdGrupo);
                }
                return a.NombreGrupo.localeCompare(b.NombreGrupo);
            });
        }

        const schoolEntries = Array.from(bySchool.entries());
        schoolEntries.sort(([ida], [idb]) => {
            if (currentEscId !== null) {
                if (ida === currentEscId) return -1;
                if (idb === currentEscId) return 1;
            }
            const nameA = _DICT_ESCUELAS.get(ida)?.Nombre || String(ida);
            const nameB = _DICT_ESCUELAS.get(idb)?.Nombre || String(idb);
            return nameA.localeCompare(nameB);
        });

        return schoolEntries;
    }

    private toggleGroup(idGrupo: number, e?: Event) {
        const next = new Set(this.workingSelection);
        if (next.has(idGrupo)) {
            next.delete(idGrupo);
            this.selectionOrder = this.selectionOrder.filter(id => id !== idGrupo);
        } else {
            next.add(idGrupo);
            this.selectionOrder = [...this.selectionOrder.filter(id => id !== idGrupo), idGrupo];
        }
        this.workingSelection = next;

        (e?.currentTarget as HTMLElement)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        this.scheduleEmit();
    }

    private toggleSelectAll() {
        const allIds = (this.availableGroups || []).map(g => g.IdGrupo);
        const allSelected = allIds.every(id => this.workingSelection.has(id));
        if (allSelected) {
            this.workingSelection = new Set();
            this.selectionOrder = [];
        } else {
            this.workingSelection = new Set(allIds);
            this.selectionOrder = [
                ...this.selectionOrder.filter(id => allIds.includes(id)),
                ...allIds.filter(id => !this.selectionOrder.includes(id))
            ];
        }
        this.scheduleEmit();
    }

    private scheduleEmit() {
        this.clearDebounce();
        this.debounceTimer = setTimeout(() => this.emitChanges(), SELECTION_EMIT_DEBOUNCE_MS);
    }

    private clearDebounce() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }

    /** Compara la selección de trabajo contra la última confirmada e invoca el delegado por cada diferencia. */
    private emitChanges() {
        this.debounceTimer = null;
        if (!this.onSelectGroup) return;

        const changedIds = new Set<number>();
        this.workingSelection.forEach(id => { if (!this.confirmedSelection.has(id)) changedIds.add(id); });
        this.confirmedSelection.forEach(id => { if (!this.workingSelection.has(id)) changedIds.add(id); });

        if (!changedIds.size) return;

        this.confirmedSelection = new Set(this.workingSelection);
        changedIds.forEach(id => this.onSelectGroup?.(id));
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'app-proximidad-group-filter': ProximidadGroupFilter;
    }
}
